import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Ticket, TicketStatus, Location, JobTag, TicketType } from '../../types/ticket';
import type { Priority } from '../../types/ticket';
import { TicketTable } from './TicketTable';
import { TicketFilters } from './TicketFilters';
import { BulkActionsBar } from './BulkActionsBar';
import { TicketDetailDrawer } from '../../components/TicketDetailDrawer';
import { useTicketing } from '../../contexts/TicketingContext';
import { useTheme } from '../../contexts/ThemeContext';
import { calculateRawUrgencyScore } from '../../utils/priorityScore';
import './AllTicketsView.css';

export interface AllTicketsFilters {
  searchQuery: string;
  priorities: Priority[];
  statuses: TicketStatus[];
  techIds: string[];
  locations: Location[];
  tags: JobTag[];
  ticketTypes: TicketType[];
  slaStatus: 'all' | 'ok' | 'at-risk' | 'breached';
  dateRange: 'all' | 'today' | 'week' | 'month';
}

export type SortField = 'urgency' | 'ticketNumber' | 'priority' | 'status' | 'tech' | 'sla' | 'created';
export type SortDirection = 'asc' | 'desc';

const DEFAULT_FILTERS: AllTicketsFilters = {
  searchQuery: '',
  priorities: [],
  statuses: [],
  techIds: [],
  locations: [],
  tags: [],
  ticketTypes: [],
  slaStatus: 'all',
  dateRange: 'all',
};

const getSlaStatus = (ticket: Ticket): 'ok' | 'at-risk' | 'breached' => {
  if (!ticket.slaDeadline) return 'ok';
  const now = new Date();
  const deadline = new Date(ticket.slaDeadline);
  const minutesRemaining = (deadline.getTime() - now.getTime()) / (1000 * 60);

  if (minutesRemaining <= 0) return 'breached';
  if (minutesRemaining <= 60) return 'at-risk';
  return 'ok';
};

const isWithinDateRange = (date: Date, range: AllTicketsFilters['dateRange']): boolean => {
  if (range === 'all') return true;

  const now = new Date();
  const ticketDate = new Date(date);

  switch (range) {
    case 'today':
      return ticketDate.toDateString() === now.toDateString();
    case 'week': {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return ticketDate >= weekAgo;
    }
    case 'month': {
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return ticketDate >= monthAgo;
    }
    default:
      return true;
  }
};

export function AllTicketsView() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const {
    tickets,
    techs,
    handleReassignTicket: onReassign,
    handleHoldTicket: onHold,
    handleChangePriority: onChangePriority,
  } = useTicketing();
  const [filters, setFilters] = useState<AllTicketsFilters>(DEFAULT_FILTERS);
  const [sortField, setSortField] = useState<SortField>('urgency');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<string>>(new Set());
  const [expandedTicketId, setExpandedTicketId] = useState<string | null>(null);
  const [detailPanelTicket, setDetailPanelTicket] = useState<Ticket | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Filter tickets
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      // Search query
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const matchesSearch =
          ticket.ticketNumber.toLowerCase().includes(query) ||
          ticket.shortDescription.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      // Priority filter
      if (filters.priorities.length > 0 && !filters.priorities.includes(ticket.priority)) {
        return false;
      }

      // Status filter
      if (filters.statuses.length > 0 && !filters.statuses.includes(ticket.status)) {
        return false;
      }

      // Tech filter
      if (filters.techIds.length > 0) {
        if (!ticket.assignedTechId || !filters.techIds.includes(ticket.assignedTechId)) {
          return false;
        }
      }

      // Location filter
      if (filters.locations.length > 0 && !filters.locations.includes(ticket.location)) {
        return false;
      }

      // Tags filter
      if (filters.tags.length > 0) {
        const ticketTags = ticket.tags ?? [];
        const hasMatchingTag = filters.tags.some(tag => ticketTags.includes(tag));
        if (!hasMatchingTag) return false;
      }

      // Ticket type filter
      if (filters.ticketTypes.length > 0 && !filters.ticketTypes.includes(ticket.ticketType)) {
        return false;
      }

      // SLA status filter
      if (filters.slaStatus !== 'all') {
        const ticketSlaStatus = getSlaStatus(ticket);
        if (ticketSlaStatus !== filters.slaStatus) return false;
      }

      // Date range filter
      if (!isWithinDateRange(ticket.createdAt, filters.dateRange)) {
        return false;
      }

      return true;
    });
  }, [tickets, filters]);

  // Sort tickets
  const sortedTickets = useMemo(() => {
    const sorted = [...filteredTickets].sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'urgency':
          comparison = calculateRawUrgencyScore(b) - calculateRawUrgencyScore(a);
          break;
        case 'ticketNumber':
          comparison = a.ticketNumber.localeCompare(b.ticketNumber);
          break;
        case 'priority': {
          const priorityOrder = { P1: 0, P2: 1, P3: 2, P4: 3 };
          comparison = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        }
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'tech': {
          const techA = techs.find(t => t.id === a.assignedTechId)?.name || 'zzz';
          const techB = techs.find(t => t.id === b.assignedTechId)?.name || 'zzz';
          comparison = techA.localeCompare(techB);
          break;
        }
        case 'sla': {
          const slaA = a.slaDeadline ? new Date(a.slaDeadline).getTime() : Infinity;
          const slaB = b.slaDeadline ? new Date(b.slaDeadline).getTime() : Infinity;
          comparison = slaA - slaB;
          break;
        }
        case 'created':
          comparison = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [filteredTickets, sortField, sortDirection, techs]);

  // Paginate tickets
  const paginatedTickets = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedTickets.slice(startIndex, startIndex + pageSize);
  }, [sortedTickets, currentPage, pageSize]);

  const totalPages = Math.ceil(sortedTickets.length / pageSize);

  // Handlers
  const handleSort = useCallback((field: SortField) => {
    if (field === sortField) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'urgency' || field === 'created' ? 'desc' : 'asc');
    }
  }, [sortField]);

  const handleSelectTicket = useCallback((ticketId: string, selected: boolean) => {
    setSelectedTicketIds(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(ticketId);
      } else {
        next.delete(ticketId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      setSelectedTicketIds(new Set(paginatedTickets.map(t => t.id)));
    } else {
      setSelectedTicketIds(new Set());
    }
  }, [paginatedTickets]);

  const handleExpandTicket = useCallback((ticketId: string | null) => {
    setExpandedTicketId(prev => prev === ticketId ? null : ticketId);
  }, []);

  const handleViewDetails = useCallback((ticket: Ticket) => {
    setDetailPanelTicket(ticket);
  }, []);

  const handleOpenFullView = useCallback((ticketId: string) => {
    setDetailPanelTicket(null);
    navigate(`/ticketing/ticket/${ticketId}`);
  }, [navigate]);

  const handleBulkReassign = useCallback(() => {
    // For now, just reassign the first selected ticket
    // In a real app, you'd open a modal to select tech for all
    const firstTicketId = Array.from(selectedTicketIds)[0];
    if (firstTicketId) {
      onReassign(firstTicketId);
    }
  }, [selectedTicketIds, onReassign]);

  const handleBulkHold = useCallback(() => {
    selectedTicketIds.forEach(ticketId => {
      onHold(ticketId, 'Bulk action');
    });
    setSelectedTicketIds(new Set());
  }, [selectedTicketIds, onHold]);

  const handleClearSelection = useCallback(() => {
    setSelectedTicketIds(new Set());
  }, []);

  const handleFiltersChange = useCallback((newFilters: AllTicketsFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  }, []);

  const handleClearFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
    setCurrentPage(1);
  }, []);

  const handleExport = useCallback(() => {
    // Generate CSV
    const headers = ['Ticket #', 'Description', 'Priority', 'Status', 'Tech', 'Location', 'SLA', 'Created'];
    const rows = sortedTickets.map(ticket => {
      const tech = techs.find(t => t.id === ticket.assignedTechId);
      const slaStatus = getSlaStatus(ticket);
      return [
        ticket.ticketNumber,
        `"${ticket.shortDescription.replace(/"/g, '""')}"`,
        ticket.priority,
        ticket.status,
        tech?.name || 'Unassigned',
        ticket.location,
        slaStatus,
        new Date(ticket.createdAt).toLocaleDateString(),
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sortedTickets, techs]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    setSelectedTicketIds(new Set()); // Clear selection on page change
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setSelectedTicketIds(new Set());
  }, []);

  // Count active filters
  const activeFilterCount =
    filters.priorities.length +
    filters.statuses.length +
    filters.techIds.length +
    filters.locations.length +
    filters.tags.length +
    filters.ticketTypes.length +
    (filters.slaStatus !== 'all' ? 1 : 0) +
    (filters.dateRange !== 'all' ? 1 : 0);

  // Stats for header
  const stats = useMemo(() => {
    const breached = tickets.filter(t => getSlaStatus(t) === 'breached').length;
    const atRisk = tickets.filter(t => getSlaStatus(t) === 'at-risk').length;
    return { breached, atRisk };
  }, [tickets]);

  return (
    <div className="all-tickets-view" data-theme={theme}>
      <div className="all-tickets-view__container">
        {/* Header */}
        <div className="all-tickets-view__header">
          <div className="all-tickets-view__header-content">
            <h1 className="all-tickets-view__title">
              All Tickets
              <span className="all-tickets-view__count">{tickets.length}</span>
            </h1>
            <p className="all-tickets-view__subtitle">
              View and manage all tickets across your team
            </p>
          </div>
          <button
            className="all-tickets-view__export-btn"
            onClick={handleExport}
            title="Export to CSV"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 10v3a1 1 0 01-1 1H3a1 1 0 01-1-1v-3M11 6l-3 3-3-3M8 9V2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Export
          </button>
        </div>

        {/* SLA Status Indicators */}
        {(stats.breached > 0 || stats.atRisk > 0) && (
          <div className="all-tickets-view__header-stats">
            {stats.breached > 0 && (
              <div className="all-tickets-view__stat all-tickets-view__stat--breached">
                <span className="all-tickets-view__stat-value">{stats.breached}</span>
                <span className="all-tickets-view__stat-label">SLA Breached</span>
              </div>
            )}
            {stats.atRisk > 0 && (
              <div className="all-tickets-view__stat all-tickets-view__stat--at-risk">
                <span className="all-tickets-view__stat-value">{stats.atRisk}</span>
                <span className="all-tickets-view__stat-label">At Risk</span>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <TicketFilters
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          techs={techs}
          activeFilterCount={activeFilterCount}
        />

        {/* Results info / Bulk Actions Bar - same row, swap content */}
        <div className="all-tickets-view__results-row">
          {selectedTicketIds.size > 0 ? (
            <BulkActionsBar
              selectedCount={selectedTicketIds.size}
              onReassign={handleBulkReassign}
              onHold={handleBulkHold}
              onClearSelection={handleClearSelection}
            />
          ) : (
            <div className="all-tickets-view__results-info">
              <span className="all-tickets-view__results-count">
                Showing {paginatedTickets.length} of {sortedTickets.length} tickets
                {sortedTickets.length !== tickets.length && (
                  <span className="all-tickets-view__filtered-note"> (filtered from {tickets.length})</span>
                )}
              </span>
            </div>
          )}
        </div>

        {/* Table */}
        <TicketTable
          tickets={paginatedTickets}
          techs={techs}
          selectedTicketIds={selectedTicketIds}
          expandedTicketId={expandedTicketId}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          onSelectTicket={handleSelectTicket}
          onSelectAll={handleSelectAll}
          onExpandTicket={handleExpandTicket}
          onViewDetails={handleViewDetails}
          onReassign={onReassign}
          onHold={onHold}
          onChangePriority={onChangePriority}
          getSlaStatus={getSlaStatus}
        />

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="all-tickets-view__pagination">
            <div className="all-tickets-view__page-size">
              <label htmlFor="page-size">Rows per page:</label>
              <select
                id="page-size"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            <div className="all-tickets-view__page-nav">
              <button
                className="all-tickets-view__page-btn"
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                title="First page"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M11 12L7 8l4-4M5 12V4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                className="all-tickets-view__page-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M10 12L6 8l4-4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <span className="all-tickets-view__page-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="all-tickets-view__page-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                className="all-tickets-view__page-btn"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                title="Last page"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M5 4l4 4-4 4M11 4v8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      <TicketDetailDrawer
        open={Boolean(detailPanelTicket)}
        ticket={detailPanelTicket}
        tech={detailPanelTicket ? techs.find(t => t.id === detailPanelTicket.assignedTechId) : undefined}
        onClose={() => setDetailPanelTicket(null)}
        onReassign={onReassign}
        onHold={onHold}
        onOpenFullView={handleOpenFullView}
        getSlaStatus={getSlaStatus}
      />
    </div>
  );
};
